import React from 'react';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

/**
 * Componente "Avaliação" do checkout (depoimento de cliente), no modelo da Hotmart:
 * cartão com avatar redondo, nome, estrelas e o texto da avaliação em itálico.
 * - Vertical (padrão): avatar centralizado em cima, conteúdo centralizado.
 * - Horizontal: avatar à esquerda, conteúdo alinhado à esquerda.
 */

export interface ReviewConfig {
    name?: string;
    text?: string;
    stars?: number; // 1 a 5
    avatarUrl?: string;
    horizontal?: boolean;
}

interface CheckoutReviewProps {
    selected: boolean;
    config: ReviewConfig;
    onSelect: () => void;
    onDelete?: () => void;
    onDuplicate?: () => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    viewMode?: boolean; // Modo visualização (checkout, sem interações)
}

const STAR_PATH = 'M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95L12 2.5z';

export const ReviewStars: React.FC<{ value: number; size?: number; className?: string }> = ({ value, size = 22, className = '' }) => (
    <div className={`flex gap-0.5 ${className}`} aria-label={`${value} de 5 estrelas`}>
        {[1, 2, 3, 4, 5].map((i) => (
            <svg key={i} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
                <path d={STAR_PATH} fill={i <= value ? '#FBBF24' : '#E5E7EB'} />
            </svg>
        ))}
    </div>
);

const initialsOf = (name: string) =>
    name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join('') || '?';

export const ReviewCard: React.FC<{ config: ReviewConfig }> = ({ config }) => {
    const name = config.name?.trim() || 'Nome do cliente';
    const text = config.text?.trim() || 'Escreva aqui a avaliação do seu cliente.';
    const stars = Math.min(5, Math.max(1, Number(config.stars) || 5));
    const horizontal = !!config.horizontal;
    const avatarSize = horizontal ? 72 : 112;

    const avatar = config.avatarUrl ? (
        <img
            src={config.avatarUrl}
            alt={name}
            loading="lazy"
            draggable={false}
            className="rounded-full object-cover shrink-0 bg-gray-100"
            style={{ width: avatarSize, height: avatarSize }}
        />
    ) : (
        <div
            className="rounded-full shrink-0 bg-gray-200 text-gray-500 font-semibold flex items-center justify-center"
            style={{ width: avatarSize, height: avatarSize, fontSize: avatarSize / 3 }}
            aria-hidden="true"
        >
            {initialsOf(name)}
        </div>
    );

    if (horizontal) {
        return (
            <div className="w-full rounded-lg border border-gray-300 bg-white p-4 flex items-start gap-4">
                {avatar}
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-gray-900 text-base leading-tight break-words">{name}</p>
                    <ReviewStars value={stars} size={18} className="mt-1" />
                    <p className="mt-2 text-sm italic text-gray-800 leading-snug whitespace-pre-line break-words">
                        "{text}"
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full rounded-lg border border-gray-300 bg-white px-5 py-4 flex flex-col items-center text-center">
            {avatar}
            <p className="mt-3 font-bold text-gray-900 text-lg leading-tight break-words">{name}</p>
            <ReviewStars value={stars} className="mt-1 justify-center" />
            <p className="mt-2 text-[15px] italic text-gray-800 leading-snug whitespace-pre-line break-words">
                "{text}"
            </p>
        </div>
    );
};

const CheckoutReview: React.FC<CheckoutReviewProps> = ({
    selected,
    config,
    onSelect,
    onDelete,
    onDuplicate,
    dragHandleProps,
    viewMode = false,
}) => {
    if (viewMode) {
        return <ReviewCard config={config} />;
    }

    return (
        <div
            className={`relative group w-full cursor-pointer rounded-lg ${selected ? 'ring-2 ring-blue-500' : 'hover:ring-2 hover:ring-blue-300'}`}
            onClick={onSelect}
        >
            <ReviewCard config={config} />

            {selected && (
                <div className="absolute top-2 right-2 flex gap-1 z-10">
                    <span
                        {...dragHandleProps}
                        className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800 cursor-grab active:cursor-grabbing"
                        title="Arrastar"
                    >
                        <i className="ri-drag-move-2-line text-sm"></i>
                    </span>
                    {onDuplicate && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-gray-600 hover:text-gray-800"
                            title="Duplicar"
                        >
                            <i className="ri-file-copy-line text-sm"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            className="p-1.5 rounded bg-white shadow-lg border text-red-600 hover:text-red-800"
                            title="Excluir"
                        >
                            <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CheckoutReview;
